import React, {useEffect, useRef, useState, useCallback} from "react";
import {IconButton, Tooltip} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GlobalVariables from "../models/globalVariables";
import VoiceModel from "../models/voiceModel";
import {debounce} from "lodash";
import VoiceVisualizer from "./VoiceVisualizer";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

/**
 * VoiceChat component handles voice-based interactions, including speech recognition,
 * text-to-speech, and audio playback. It provides a user interface for toggling mute,
 * visualizing voice activity, and managing the voice chat session.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Function} props.onSendButtonClick - Callback function to handle sending prompts.
 * @param {Function} props.setVoiceMode - Callback function to toggle voice mode.
 *
 * @returns {JSX.Element} The rendered VoiceChat component.
 */
const VoiceChat = ({onSendButtonClick, setVoiceMode, isEmailHomePage = false}) => {
  const [isMuted, setIsMuted] = useState(false);
  const [listening, setListening] = useState(false);
  const [recognitionHasError, setRecognitionHasError] = useState(false);
  const [isGettingResponse, setIsGettingResponse] = useState(false);

  const [statusMessage, setStatusMessage] = useState("Initializing...");
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const botAudioRef = useRef(null);
  const [botAudio, setBotAudio] = useState(null);
  const [visualizerMode, setVisualizerMode] = useState("mic"); // 'mic' | 'bot'
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [speechSynthesisSupported, setSpeechSynthesisSupported] = useState(true);
  const [recognitionSupported, setRecognitionSupported] = useState(true);
  const [isStopped, setIsStopped] = useState(false);
  const stopAudioRef = useRef(false);

  // Keep up-to-date mute flag inside callbacks
  const isMutedRef = useRef(isMuted);
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Detect mobile browser
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      setIsMobile(mobileRegex.test(userAgent.toLowerCase()));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  /**
   * Plays a voice response generated from a given prompt using the VoiceModel.
   *
   * @function
   * @async
   * @param {string} prompt - The text prompt to generate the voice response from.
   * @returns {Promise<void>} - A promise that resolves when the audio playback is complete.
   * @throws {Error} - Throws an error if there is an issue generating or playing the audio.
   */
  const stopAudio = useCallback(() => {
    if (botAudio) {
      botAudio.pause();
      botAudio.currentTime = 0;
      setBotAudio(null);
      stopAudioRef.current = true;
      setIsStopped(true);
    }
  }, [botAudio]);

  const playVoiceResponse = useCallback(async (prompt) => {
    try {
      // Reset stop flag for new audio
      stopAudioRef.current = false;
      setIsStopped(false);
      
      const blob = await VoiceModel.getVoiceGoogle(prompt);
      const audioUrl = URL.createObjectURL(blob);
      setIsProcessing(false);

      // Use an audio context for iOS compatibility
      const audioElement = document.createElement("audio");
      audioElement.src = audioUrl;
      audioElement.crossOrigin = "anonymous";
      audioElement.preload = "auto";

      // iOS requires user interaction before playing audio
      if (isMobile) {
        // Create an AudioContext for iOS
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioContext();

        // Connect audio element to audio context
        const source = audioContext.createMediaElementSource(audioElement);
        source.connect(audioContext.destination);

        // Resume audio context if it's suspended (common on iOS)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Add error handling for iOS audio loading
        audioElement.onerror = (e) => {
          console.error("Audio loading error:", e);
          setStatusMessage("Error loading audio. Please try again.");
        };

        // iOS specific: Wait for canplaythrough event
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error("Audio loading timeout"));
          }, 10000);

          audioElement.oncanplaythrough = () => {
            clearTimeout(timeout);
            resolve();
          };

          audioElement.onerror = (e) => {
            clearTimeout(timeout);
            reject(e);
          };
        });
      }

      audioElement.load();

      // Set the audio element before attempting to play
      setBotAudio(audioElement);

      // Attempt to play with error handling for mobile browsers
      try {
        const playPromise = audioElement.play();

        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error("Autoplay prevented:", error);
            setStatusMessage("Tap to play response");

            if (isMobile) {
              const playButton = document.createElement("button");
              playButton.innerText = "Play Response";
              playButton.style.position = "fixed";
              playButton.style.top = "50%";
              playButton.style.left = "50%";
              playButton.style.transform = "translate(-50%, -50%)";
              playButton.style.zIndex = "1000";
              playButton.style.padding = "12px 24px";
              playButton.style.backgroundColor = "var(--primary-color)";
              playButton.style.color = "white";
              playButton.style.border = "none";
              playButton.style.borderRadius = "4px";
              playButton.style.fontSize = "16px";
              playButton.onclick = () => {
                audioElement.play().catch(e => {
                  console.error("Play error:", e);
                  setStatusMessage("Error playing audio. Please try again.");
                });
                document.body.removeChild(playButton);
              };
              document.body.appendChild(playButton);
            }
          });
        }
      } catch (playError) {
        console.error("Play error:", playError);
        setStatusMessage("Cannot autoplay. Tap to play response.");
      }

      return new Promise((resolve, reject) => {
        audioElement.onerror = () => {
          setStatusMessage("Audio playback error.");
          reject(new Error("Audio playback error."));
        };

        audioElement.onended = () => {
          if (!stopAudioRef.current) {
            setStatusMessage("Response finished.");
          }
          setBotAudio(null);
          resolve();
        };
      });
    } catch (err) {
      console.error("Voice response error:", err);
      setStatusMessage(`Audio error: ${err.message}`);
      if (isMobile) {
        setStatusMessage("Error on mobile device. Please try again or use text chat.");
      }
    }
  }, [isMobile]);

  // Speech Synthesis
  /**
   * Custom hook that provides a debounced function to perform text-to-speech synthesis.
   *
   * @returns {Function} A debounced function that takes a string of text as input and uses the Web Speech API
   *                     to synthesize speech.
   */
  const useSpeak = () => {
    const speak = useCallback(
      debounce((text) => {
        // Check if speech synthesis is supported
        if (!("speechSynthesis" in window)) {
          setSpeechSynthesisSupported(false);
          setStatusMessage("Speech synthesis not supported.");
          return;
        }

        // Cancel any ongoing speech
        window.speechSynthesis.cancel();

        // Create the SpeechSynthesisUtterance object
        const ut = new SpeechSynthesisUtterance(text);

        // Use a shorter utterance on mobile for better performance
        if (isMobile) {
          // Some mobile browsers have issues with long utterances
          const maxLength = 100;
          if (text.length > maxLength) {
            const shortenedText = text.substring(0, maxLength) + "...";
            ut.text = shortenedText;
          }
        }

        // Handle errors during speech synthesis
        ut.onerror = (e) => {
          setStatusMessage(`Speech error: ${e.error}`);
          console.error("Speech error:", e.error);
        };

        // Start speaking
        window.speechSynthesis.speak(ut);
      }, 500), // Debounce with 500ms delay
      [isMobile]
    );

    return speak;
  };

  const speak = useSpeak(); // Get the speak function from the hook

  // Send Prompt
  const handleSendPrompt = useCallback(
    async (prompt) => {
      if (!prompt) return;
      setStatusMessage("Sending prompt...");
      if (recognitionRef.current) recognitionRef.current.stop();

      try {
        setStatusMessage("Processing response...");
        setIsMuted(true);
        setVisualizerMode("bot");
        setIsProcessing(true);

        setIsGettingResponse(true);
        setStatusMessage("Generating response...");
        if (!isEmailHomePage) {
        const responseGenerator = await onSendButtonClick(
          prompt,
          "",
          false,
          [],
          true,
          playVoiceResponse
          );
        } else {
          let requestParams = {
            language: "english",
            tone: "neutral",
            user_prompt: prompt,
          };
          const responseGenerator = await onSendButtonClick(
            prompt,
            "",
            "",
            "addy-assistant-001",
            requestParams,
            false,
            [],
            true,
            playVoiceResponse
          );

        }

        // Wait for audio to finish before unmuting
        await new Promise(resolve => {
          const checkAudio = setInterval(() => {
            if (!botAudio) {
              clearInterval(checkAudio);
              resolve();
            }
          }, 100);
        });

        setIsMuted(false);
        // Give the browser a moment to reset before restarting recognition
        setTimeout(() => {
          if (recognitionRef.current && !isMutedRef.current) {
            try {
              recognitionRef.current.start();
              setVisualizerMode("mic");
              setStatusMessage("Restarting listener...");
            } catch (err) {
              if (err.name === "InvalidStateError") {
                console.warn("Recognition already started. Skipping.");
              } else {
                console.error("Error restarting recognition:", err);
                setStatusMessage(`Restart error: ${err.message}`);
              }
            }
          }
        }, 300);

        setIsGettingResponse(false);
      } catch (err) {
        setStatusMessage(`Error: ${err.message}`);
        speak(`Sorry, I encountered an error: ${err.message}`);
      }
    },
    [onSendButtonClick, speak, playVoiceResponse]
  );

  // Speech Recognition Setup
  useEffect(() => {
    if (
      !("SpeechRecognition" in window || "webkitSpeechRecognition" in window)
    ) {
      setStatusMessage("Speech recognition not supported.");
      setRecognitionSupported(false);
      return;
    }

    // If we already have a recognition instance, don't create a new one
    if (recognitionRef.current) {
      return;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recog = new SR();
    recognitionRef.current = recog;

    recog.lang = "en-US";
    recog.interimResults = false;

    // Set continuous mode based on device type
    recog.continuous = false; // Set to false for all devices to prevent duplicate requests

    if (isMobile) {
      recog.maxAlternatives = 1;
    }

    // Check for microphone permission first
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(() => {
        // Permission granted, proceed with recognition setup
        recog.onstart = () => {
          setListening(true);
          setStatusMessage("Listening...");
          finalTranscriptRef.current = "";
        };

        recog.onresult = (event) => {
          // Improved logging for debugging
          
          if (isMutedRef.current) return;
          let transcript = "";
          for (let i = event.resultIndex; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              transcript += event.results[i][0].transcript;
            }
          }
          if (transcript.trim()) {
            handleSendPrompt(transcript.trim());
          }
        };

        recog.onerror = (event) => {
          setListening(false);
          let msg = `Speech error: ${event.error}`;

          // More user-friendly messages for common errors
          if (event.error === "no-speech") msg = "No speech detected. Please try again.";
          if (event.error === "audio-capture") msg = "No microphone detected.";
          if (event.error === "not-allowed" || event.error.includes("not-allowed")) {
            msg = "Microphone permission denied. Please allow microphone access.";
          }
          if (event.error === "network") {
            msg = "Network error. Please check your connection.";
          }
          if (event.error === "aborted" && isMobile) {
            // Common on mobile, don't treat as an error
            setRecognitionHasError(false);
            return;
          }

          setRecognitionHasError(true);
          setStatusMessage(msg);
        };

        recog.onend = () => {
          setListening(false);
          if (recognitionHasError) return;

          // Add a delay before restarting on mobile to prevent rapid restart
          const restartDelay = isMobile ? 1000 : 300;

          if (!isMutedRef.current) {
            setStatusMessage("Restarting listener…");
            setTimeout(() => {
              try {
                if (recognitionRef.current) {
                  recognitionRef.current.start();
                }
              } catch (err) {
                if (err.name !== "InvalidStateError") {
                  console.error("Error restarting recognition:", err);
                }
              }
            }, restartDelay);
          } else {
            setStatusMessage("Muted. Press Unmute to talk.");
          }
        };

        setRecognitionHasError(false);
        // initial start
        if (!isMutedRef.current) {
          setStatusMessage("Starting listener...");
          try {
            recog.start();
          } catch (err) {
            setStatusMessage(`Error starting: ${err.message}`);
          }
        } else {
          setStatusMessage("Muted. Press Unmute to talk.");
        }
      })
      .catch((err) => {
        console.error("Microphone permission error:", err);
        setStatusMessage("Microphone permission denied. Please allow microphone access.");
        setRecognitionHasError(true);
      });

    return () => {
      window.speechSynthesis?.cancel();
      if (recognitionRef.current) {
        recog.onstart = recog.onresult = recog.onerror = recog.onend = null;
        try {
          recog.stop();
        } catch (e) {
          // Ignore errors when stopping
        }
        recognitionRef.current = null;
      }
    };
  }, [handleSendPrompt, isMobile]);

  // Mute / Unmute
  const toggleMute = () => {
    if (isGettingResponse) {
      setStatusMessage("Please wait for the response to finish.");
      return;
    }

    const next = !isMutedRef.current;
    setIsMuted(next);
    
    // Stop any ongoing audio processing
    stopAudio();
    
    if (recognitionRef.current) {
      if (next) {
        try {
          recognitionRef.current.stop();
        } catch (err) {
          console.error("Error stopping recognition:", err);
        }
        window.speechSynthesis?.cancel();
        setStatusMessage("Muted.");
        setVisualizerMode("");
      } else {
        setStatusMessage("Unmuting…");
        try {
          recognitionRef.current.start();
          setVisualizerMode("mic");
        } catch (err) {
          setStatusMessage(`Error starting: ${err.message}`);
        }
      }
    }
  };

  // Render mobile-friendly UI or fallback for unsupported browsers
  if (!recognitionSupported) {
    return (
      <div style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
        textAlign: "center"
      }}>
        <h2>Speech Recognition Not Supported</h2>
        <p>Your browser doesn't support speech recognition. Please try using Chrome, Edge, or Safari.</p>
        <button
          onClick={() => setVoiceMode(false)}
          style={{
            padding: "10px 20px",
            marginTop: "20px",
            background: "var(--primary-color)",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer"
          }}
        >
          Return to Text Chat
        </button>
      </div>
    );
  }

  return (
    <div style={{height: "100vh", width: "100vw", position: "relative"}}>

      {/* Visualizer - responsive size */}
      <div
        style={{
          position: "absolute",
          top: "40%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "500px",
          height: "500px",
        }}
      >
        <VoiceVisualizer
          visualizerMode={visualizerMode}
          externalAudio={botAudio}
          isProcessing={isProcessing}
          isMuted={isMuted}
        />
      </div>

      {/* Controls - larger touch targets for mobile */}
      <div
        style={{
          position: "absolute",
          bottom: isMobile ? "60px" : "20px",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          width: "100%"
        }}
      >
        <IconButton
          onClick={toggleMute}
          size={isMobile ? "large" : "medium"}
          style={{
            background: "color-mix(in srgb, var(--primary-color) 12%, transparent)",
            padding: isMobile ? "20px" : "12px",
            margin: "0 10px"
          }}
        >
          {isMuted ? (
            <MicOffIcon style={{color: "var(--text-brand)", fontSize: isMobile ? "32px" : "24px"}} />
          ) : (
            <MicIcon style={{color: "var(--text-brand)", fontSize: isMobile ? "32px" : "24px"}} />
          )}
        </IconButton>
        <Tooltip title={isGettingResponse || isProcessing || botAudio ? "Processing..." : "Stop"}>
          <IconButton
            onClick={() => setVoiceMode(false)}
            size={isMobile ? "large" : "medium"}
            style={{
              background: "var(--surface-muted)",
              padding: isMobile ? "20px" : "12px",
              margin: "0 10px"
            }}
            //disable if bot is processing or answering
            disabled={isGettingResponse || isProcessing || botAudio}
          >
            <CloseIcon style={{
              color: (isGettingResponse || isProcessing || botAudio) ? "color-mix(in srgb, var(--primary-color) 25%, transparent)" : "var(--primary-color)",
              fontSize: isMobile ? "32px" : "24px"
            }} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

export default VoiceChat;